<script>
import {
  defineComponent,
  h,
  ref,
  computed,
  onBeforeUnmount,
  onMounted,
} from 'vue'
import tippy, { followCursor } from 'tippy.js'
import { placement } from './config'
import config from '../../utils/config'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates.
   * @example
   * <ak-tooltip />
   */
  name: 'AkTooltip',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Determines the position of the tooltip.
     */
    placement: {
      validator: (o) => placement.indexOf(o) >= 0,
      type: String,
      default: config.defaultTooltipPosition,
    },

    /**
     * @type {Boolean}
     * @description Determines if the contnainer for the tooltip should
     * have a style to look like a link (handy for tooltips on plain text).
     */
    isLink: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Determines the color of the tooltip background.
     */
    theme: {
      validator: (o) => ['light', 'dark'].indexOf(o) >= 0,
      type: String,
      default: 'dark',
    },

    /**
     * @type {String}
     * @description Determines if the tooltip should position itself
     * at the cursor position.
     */
    followCursor: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String|Boolean}
     * @description Determines if the tooltip should hide on click.
     */
    hideOnClick: {
      validator: (o) => [true, false, 'toggle'].indexOf(o) >= 0,
      type: [String, Boolean],
      default: false,
    },

    /**
     * @type {String|Number}
     * @description Determines the max width of the popover.
     */
    maxWidth: {
      type: [String, Number],
      default: 'none',
    },

    /**
     * @type {String}
     * @description The type of trigger to use for the tooltip.
     */
    trigger: {
      type: String,
      default: 'mouseenter focus',
    },
    hasArrow: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {Number}
     * @description Move tooltip on X axis
     */
    xAxis: {
      type: Number,
      default: 0,
    },
    /**
     * @type {Number}
     * @description Move tooltip on Y axis
     */
    yAxis: {
      type: Number,
      default: 8,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.slots - The slots for the component to render
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { slots }) {
    const tooltip = ref(null)
    const trigger = ref(null)
    let instance = null

    /**
     * @return {Object}
     * @description Returns the classes for our tooltip.
     */
    const classes = computed(() => {
      return {
        'ak-tooltip': true,
        'ak-tooltip--is-link': props.isLink,
      }
    })

    const init = () => {
      instance = tippy(trigger.value, {
        allowHTML: true,
        arrow: props.hasArrow,
        hideOnClick: props.hideOnClick,
        ignoreAttributes: true,
        placement: props.placement,
        theme: props.theme,
        followCursor: props.followCursor,
        maxWidth: props.maxWidth,
        trigger: props.trigger,
        plugins: [followCursor],
        duration: 100,
        delay: [100, 0],
        offset: [props.xAxis, props.yAxis],
        content: () => {
          return tooltip.value.innerHTML
        },
      })
    }

    onMounted(() => init())
    onBeforeUnmount(() => instance.destroy())

    return () =>
      h('span', { ref: trigger, class: classes.value }, [
        slots.trigger(),
        h(
          'span',
          { ref: tooltip, style: { display: 'none' } },
          slots.default()
        ),
      ])
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';
@import '~tippy.js/dist/tippy.css';
@import '~tippy.js/themes/light.css';

.ak-tooltip {
  display: inline-block;

  &--is-link {
    border-bottom: 1px dotted $color-grey-dark;
  }
}

.tippy-box {
  box-shadow: $base-shadow;
  background-color: $color-grey-dark;
}
.tippy-box[data-theme~='light'] {
  color: $color-text;
  box-shadow: $base-shadow;
  background-color: #fff;
}
.tippy-content {
  padding: $spacing-tiny $spacing-small;
  font-size: $font-size-small;
}
</style>
